<template>
    <section
        :id="slice.primary.anchorName || undefined"
        :class="[
            'slice slice-form slice-form--form-with-accordion',
            {'slice-form--grey': backgroundColor === 'grey'},
            {'slice-form--hide-in-mobile': slice.primary.hideInMobile},
        ]"
        aria-label="form"
        data-testid="slice-form-with-accordion"
    >
        <div class="slice-content">
            <div class="slice-form__content" :class="{'slice-form__content--focused': isFocused}">
                <MktTitleBrand :field="slice.primary.title" class="slice-form__title slice-title" data-testid="slice-form-title" />
                <div
                    v-if="Array.isArray(slice.primary.description) && isFilled.richText(slice.primary.description)"
                    class="slice-description"
                    data-testid="slice-form-description"
                >
                    <PrismicRichText :field="slice.primary.description" />
                </div>
                <div data-testid="slice-form-hubspot-animated">
                    <MktHubspotFormAnimated
                        :region="slice.primary.hubspotRegion || ''"
                        :portal-id="slice.primary.hubspotPortalId || ''"
                        :form-id="slice.primary.hubspotFormId || ''"
                        :salesforce-id="slice.primary.salesforceId || ''"
                        :visible-field-name="slice.primary.visibleFieldName || 'email'"
                        @focusin="onFocusIn"
                        @focusout="onFocusOut"
                        @submit="onFormSubmit"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, inject, ref} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {FormSlice} from '@prismicio-types';
    import {MktHubspotFormAnimated, MktTitleBrand} from '#components';
    import {useDataTracking} from '#imports';

    type FormSliceWithAccordionInZone = Extract<FormSlice, {variation: 'formWithAccordion'}>;
    const props = defineProps(getSliceComponentProps<FormSliceWithAccordionInZone>(['slice', 'index', 'slices', 'context']));
    const {slice} = props;

    const backgroundColor = computed(() => slice.primary.backgroundColor);

    const {track, extractWebAttributionProperties} = useDataTracking();
    const pageCategory = inject('pageCategory');

    const isFocused = ref(false);

    function onFocusIn() {
        isFocused.value = true;
    }
    function onFocusOut() {
        isFocused.value = false;
    }

    function onFormSubmit(form: HTMLFormElement) {
        const h1 = document.querySelector('h1');
        const typeField = form.querySelector(`.hs-input[name=form_type]`) as HTMLInputElement;
        const eventProperties = {
            url: window.location.pathname,
            page_title_h1: h1?.innerText,
            page_category: pageCategory,
            form_type: typeField?.value || null,
            form_name: form.dataset.formId,
        };
        const userProperties = {
            ...extractWebAttributionProperties(),
            referrer: document.referrer || null,
        };
        track('website_form_submitted', eventProperties, userProperties);
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-form {
        &--hide-in-mobile {
            display: none;

            @include mq.screen_sm(min) {
                display: block;
            }
        }

        &--form-with-accordion {
            padding: 0;

            @include mq.screen_md(min) {
                padding: 0 var(--joy-core-spacing-6);
            }

            &.slice-form--grey {
                background-color: var(--joy-color-neutral-20);
            }

            .slice-form__content {
                background-color: var(--joy-color-primary-50);
                text-align: center;
                margin-bottom: var(--joy-core-spacing-12);
                padding: var(--joy-core-spacing-9) var(--joy-core-spacing-5);
                border-radius: var(--joy-core-radius-4);

                &,
                .slice-form__title,
                .slice-form__title * {
                    color: var(--joy-color-neutral-0);
                    font-size: 32px;
                    font-weight: var(--joy-font-weight-normal);
                    line-height: var(--joy-line-height-small);
                }

                .slice-form__title {
                    margin-bottom: var(--joy-core-spacing-5);
                }

                @include mq.screen_md(min) {
                    display: flex;
                    flex-direction: row;
                    text-align: left;
                    padding: var(--joy-core-spacing-9) var(--joy-core-spacing-12);

                    .slice-form__title {
                        width: 340px;
                        padding-right: var(--joy-core-spacing-4);
                        flex: none;
                        margin-bottom: 0;
                    }

                    .slice-form__title * {
                        font-size: var(--joy-font-size-secondary-400);
                    }

                    .hubspot-form-animated__wrapper {
                        flex: 1;
                    }

                    &:not(&--focused) {
                        .hubspot-form-animated__wrapper {
                            display: flex;
                            align-items: center;
                        }
                    }
                }

                @include mq.screen_lg(min) {
                    .slice-form__title {
                        width: 390px;
                        padding-right: var(--joy-core-spacing-6);
                    }
                }
            }

            .hubspot-form.mkt-hubspot-form-animated {
                label,
                .hs-richtext,
                .hs-form-field .hs-error-msgs,
                .hs-main-font-element {
                    &,
                    * {
                        color: var(--joy-color-neutral-0) !important;
                    }
                }

                .hs-form-field .hs-input {
                    background-color: var(--joy-color-neutral-10);
                    border-color: var(--joy-color-neutral-10);
                }

                .hs-fieldtype-text .hs-input,
                .hs-fieldtype-select .hs-input {
                    height: 60px;
                }

                .hs-button {
                    background-color: transparent;
                    border-color: var(--joy-color-neutral-10);

                    &:hover {
                        background-color: var(--joy-color-primary-70);
                    }
                }

                .hs-main-font-element {
                    font-family: var(--joy-font-family-base) !important;
                    font-size: var(--joy-font-size-primary-400) !important;
                    font-weight: var(--joy-font-weight-normal) !important;
                    line-height: var(--joy-line-height-large) !important;
                }
            }
        }
    }
</style>
